import { DownOutlined, FileExcelOutlined, FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import { Card, Spin, Alert, Button, Dropdown, Menu, Segmented } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { getPartyWiseProfitLoss } from "./account.api";
import "./account.css";

const FILTER_CUSTOMER = "customer";
const FILTER_SUPPLIER = "supplier";

const formatAmount = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const buildPartyWiseRows = (data, filter) => {
  const fmt = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
  if (filter === FILTER_CUSTOMER) {
    const rows = (data?.customerWise || []).map((r) => [
      r.name || "—",
      fmt(r.totalSales),
      fmt(r.totalLineDiscount),
      fmt(r.totalAdditionalDiscount),
      fmt(r.profit),
    ]);
    if (rows.length > 0) {
      const totalSales = data.customerWise.reduce((s, r) => s + Number(r.totalSales) || 0, 0);
      const totalLine = data.customerWise.reduce((s, r) => s + Number(r.totalLineDiscount) || 0, 0);
      const totalAdd = data.customerWise.reduce((s, r) => s + Number(r.totalAdditionalDiscount) || 0, 0);
      const totalProfit = data.customerWise.reduce((s, r) => s + Number(r.profit) || 0, 0);
      rows.push(["TOTAL", fmt(totalSales), fmt(totalLine), fmt(totalAdd), fmt(totalProfit)]);
    }
    return {
      headers: ["Customer", "Total Sales (Gross)", "Discount", "Add. Discount", "Profit"],
      rows,
    };
  }
  const rows = (data?.supplierWise || []).map((r) => [
    r.name || "—",
    fmt(r.totalPurchases),
    fmt(r.totalLineDiscount),
    fmt(r.totalAdditionalDiscount),
  ]);
  if (rows.length > 0) {
    const total = data.supplierWise.reduce((s, r) => s + Number(r.totalPurchases) || 0, 0);
    const totalLine = data.supplierWise.reduce((s, r) => s + Number(r.totalLineDiscount) || 0, 0);
    const totalAdd = data.supplierWise.reduce((s, r) => s + Number(r.totalAdditionalDiscount) || 0, 0);
    rows.push(["TOTAL", fmt(total), fmt(totalLine), fmt(totalAdd)]);
  }
  return {
    headers: ["Supplier", "Total Purchases", "Discount", "Add. Discount"],
    rows,
  };
};

const exportPDF = (data, filter) => {
  const { headers, rows } = buildPartyWiseRows(data, filter);
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  doc.setFontSize(12);
  doc.text(filter === FILTER_CUSTOMER ? "Party-wise Profit and Loss (Customer)" : "Party-wise Profit and Loss (Supplier)", 40, 30);
  doc.autoTable({
    head: [headers],
    body: rows,
    startY: 40,
    theme: "grid",
    headStyles: { fillColor: [0, 0, 0], fontSize: 9, textColor: [255, 255, 255] },
    bodyStyles: { fontSize: 8 },
  });
  doc.save(`party-wise-profit-loss-${filter}.pdf`);
};

const exportExcel = (data, filter) => {
  const { headers, rows } = buildPartyWiseRows(data, filter);
  const sheetData = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, filter === FILTER_CUSTOMER ? "Customer" : "Supplier");
  XLSX.writeFile(wb, `party-wise-profit-loss-${filter}.xlsx`);
};

const handlePrint = () => {
  window.print();
};

const PartyWiseProfitLoss = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(FILTER_CUSTOMER);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getPartyWiseProfitLoss();
        setData(result);
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to fetch party-wise profit and loss");
        console.error("Error loading party-wise P&L:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="pwpl-page">
        <Card className="pwpl-card">
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "50px" }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pwpl-page">
        <Card className="pwpl-card">
          <Alert message="Error" description={error} type="error" showIcon />
        </Card>
      </div>
    );
  }

  const customerWise = data?.customerWise || [];
  const supplierWise = data?.supplierWise || [];
  const totalCustomerSales = customerWise.reduce((s, r) => s + (Number(r.totalSales) || 0), 0);
  const totalCustomerLineDisc = customerWise.reduce((s, r) => s + (Number(r.totalLineDiscount) || 0), 0);
  const totalCustomerAddDisc = customerWise.reduce((s, r) => s + (Number(r.totalAdditionalDiscount) || 0), 0);
  const totalCustomerProfit = customerWise.reduce((s, r) => s + (Number(r.profit) || 0), 0);
  const totalSupplierPurchases = supplierWise.reduce((s, r) => s + (Number(r.totalPurchases) || 0), 0);
  const totalSupplierLineDisc = supplierWise.reduce((s, r) => s + (Number(r.totalLineDiscount) || 0), 0);
  const totalSupplierAddDisc = supplierWise.reduce((s, r) => s + (Number(r.totalAdditionalDiscount) || 0), 0);

  return (
    <div className="pwpl-page">
      <Card className="pwpl-card pwpl-card-modern" bordered={false}>
        <div className="erp-no-print pwpl-header pwpl-header--compact-row">
          <div className="pwpl-header-compact-left">
            <h1 className="pwpl-main-title">Party-wise Profit and Loss Report</h1>
            <div className="pwpl-header-compact-segmented">
              <Segmented
                className="pwpl-segmented"
                options={[
                  { label: "Customer wise (Sales & Profit)", value: FILTER_CUSTOMER },
                  { label: "Supplier wise (Purchases)", value: FILTER_SUPPLIER },
                ]}
                value={filter}
                onChange={setFilter}
              />
            </div>
          </div>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="pdf" icon={<FilePdfOutlined />} onClick={() => exportPDF(data, filter)}>
                  Download PDF
                </Menu.Item>
                <Menu.Item key="excel" icon={<FileExcelOutlined />} onClick={() => exportExcel(data, filter)}>
                  Download Excel
                </Menu.Item>
                <Menu.Item key="print" icon={<PrinterOutlined />} onClick={handlePrint}>
                  Print
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="primary" size="small" className="pwpl-export-btn">
              Download / Print <DownOutlined />
            </Button>
          </Dropdown>
        </div>

        <div className="pwpl-screen-tables">
          <div className="pwpl-table-wrap">
            {filter === FILTER_CUSTOMER && (
              <table className="pwpl-table">
                <thead>
                  <tr>
                    <th>CUSTOMER</th>
                    <th className="pwpl-th-amount">TOTAL SALES (GROSS)</th>
                    <th className="pwpl-th-amount">DISCOUNT</th>
                    <th className="pwpl-th-amount">ADD. DISCOUNT</th>
                    <th className="pwpl-th-amount">PROFIT</th>
                  </tr>
                </thead>
                <tbody>
                  {customerWise.length > 0 ? (
                    <>
                      {customerWise.map((row, index) => (
                        <tr key={row.id ?? `c-${index}`}>
                          <td className="pwpl-name">{row.name || "—"}</td>
                          <td className="pwpl-amount">{formatAmount(row.totalSales)}</td>
                          <td className="pwpl-amount">{formatAmount(row.totalLineDiscount)}</td>
                          <td className="pwpl-amount">{formatAmount(row.totalAdditionalDiscount)}</td>
                          <td className="pwpl-amount">{formatAmount(row.profit)}</td>
                        </tr>
                      ))}
                      <tr className="pwpl-total-row">
                        <td><strong>TOTAL</strong></td>
                        <td className="pwpl-amount"><strong>{formatAmount(totalCustomerSales)}</strong></td>
                        <td className="pwpl-amount"><strong>{formatAmount(totalCustomerLineDisc)}</strong></td>
                        <td className="pwpl-amount"><strong>{formatAmount(totalCustomerAddDisc)}</strong></td>
                        <td className="pwpl-amount"><strong>{formatAmount(totalCustomerProfit)}</strong></td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={5}>No customer data</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {filter === FILTER_SUPPLIER && (
              <table className="pwpl-table">
                <thead>
                  <tr>
                    <th>SUPPLIER</th>
                    <th className="pwpl-th-amount">TOTAL PURCHASES</th>
                    <th className="pwpl-th-amount">DISCOUNT</th>
                    <th className="pwpl-th-amount">ADD. DISCOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierWise.length > 0 ? (
                    <>
                      {supplierWise.map((row, index) => (
                        <tr key={row.id ?? `s-${index}`}>
                          <td className="pwpl-name">{row.name || "—"}</td>
                          <td className="pwpl-amount">{formatAmount(row.totalPurchases)}</td>
                          <td className="pwpl-amount">{formatAmount(row.totalLineDiscount)}</td>
                          <td className="pwpl-amount">{formatAmount(row.totalAdditionalDiscount)}</td>
                        </tr>
                      ))}
                      <tr className="pwpl-total-row">
                        <td><strong>TOTAL</strong></td>
                        <td className="pwpl-amount"><strong>{formatAmount(totalSupplierPurchases)}</strong></td>
                        <td className="pwpl-amount"><strong>{formatAmount(totalSupplierLineDisc)}</strong></td>
                        <td className="pwpl-amount"><strong>{formatAmount(totalSupplierAddDisc)}</strong></td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan={4}>No supplier data</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className="pwpl-footer">
            <span className="pwpl-summary">
              {filter === FILTER_CUSTOMER
                ? `${customerWise.length} customers | Sales (Gross): ${formatAmount(totalCustomerSales)} | Line Disc.: ${formatAmount(totalCustomerLineDisc)} | Add. Disc.: ${formatAmount(totalCustomerAddDisc)} | Profit: ${formatAmount(totalCustomerProfit)}`
                : `${supplierWise.length} suppliers | Purchases: ${formatAmount(totalSupplierPurchases)} | Line Disc.: ${formatAmount(totalSupplierLineDisc)} | Add. Disc.: ${formatAmount(totalSupplierAddDisc)}`}
            </span>
          </div>
        </div>

        {/* Print-only: show the selected tab only (matches download) */}
        <div className="pwpl-print-all-tables" aria-hidden="true">
          {filter === FILTER_CUSTOMER && (
            <>
              <div className="pwpl-print-doc-title">Party-wise Profit and Loss (Customer)</div>
              <div className="pwpl-table-wrap">
                <table className="pwpl-table">
                  <thead>
                    <tr>
                      <th>CUSTOMER</th>
                      <th className="pwpl-th-amount">TOTAL SALES (GROSS)</th>
                      <th className="pwpl-th-amount">DISCOUNT</th>
                      <th className="pwpl-th-amount">ADD. DISCOUNT</th>
                      <th className="pwpl-th-amount">PROFIT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerWise.length > 0 ? (
                      <>
                        {customerWise.map((row, index) => (
                          <tr key={`print-c-${row.id ?? index}`}>
                            <td className="pwpl-name">{row.name || "—"}</td>
                            <td className="pwpl-amount">{formatAmount(row.totalSales)}</td>
                            <td className="pwpl-amount">{formatAmount(row.totalLineDiscount)}</td>
                            <td className="pwpl-amount">{formatAmount(row.totalAdditionalDiscount)}</td>
                            <td className="pwpl-amount">{formatAmount(row.profit)}</td>
                          </tr>
                        ))}
                        <tr className="pwpl-total-row">
                          <td><strong>TOTAL</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalCustomerSales)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalCustomerLineDisc)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalCustomerAddDisc)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalCustomerProfit)}</strong></td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan={5}>No customer data</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="pwpl-footer">
                <span className="pwpl-summary">
                  {customerWise.length} customers | Sales (Gross): {formatAmount(totalCustomerSales)} | Line Disc.: {formatAmount(totalCustomerLineDisc)} | Add. Disc.: {formatAmount(totalCustomerAddDisc)} | Profit: {formatAmount(totalCustomerProfit)}
                </span>
              </div>
            </>
          )}

          {filter === FILTER_SUPPLIER && (
            <>
              <div className="pwpl-print-doc-title">Party-wise Profit and Loss (Supplier)</div>
              <div className="pwpl-table-wrap">
                <table className="pwpl-table">
                  <thead>
                    <tr>
                      <th>SUPPLIER</th>
                      <th className="pwpl-th-amount">TOTAL PURCHASES</th>
                      <th className="pwpl-th-amount">DISCOUNT</th>
                      <th className="pwpl-th-amount">ADD. DISCOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierWise.length > 0 ? (
                      <>
                        {supplierWise.map((row, index) => (
                          <tr key={`print-s-${row.id ?? index}`}>
                            <td className="pwpl-name">{row.name || "—"}</td>
                            <td className="pwpl-amount">{formatAmount(row.totalPurchases)}</td>
                            <td className="pwpl-amount">{formatAmount(row.totalLineDiscount)}</td>
                            <td className="pwpl-amount">{formatAmount(row.totalAdditionalDiscount)}</td>
                          </tr>
                        ))}
                        <tr className="pwpl-total-row">
                          <td><strong>TOTAL</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalSupplierPurchases)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalSupplierLineDisc)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalSupplierAddDisc)}</strong></td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan={4}>No supplier data</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="pwpl-footer">
                <span className="pwpl-summary">
                  {supplierWise.length} suppliers | Purchases: {formatAmount(totalSupplierPurchases)} | Line Disc.: {formatAmount(totalSupplierLineDisc)} | Add. Disc.: {formatAmount(totalSupplierAddDisc)}
                </span>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PartyWiseProfitLoss;
